/**
 * The merchant storefront's origin, captured from the parent window.
 *
 * In iframe mode every host-bridge response arrives via postMessage, and
 * `event.origin` on those messages IS the storefront's origin (the iframe is
 * cross-origin to it and can't read it any other way). `sendToHost` records
 * it here after verifying the message came from `window.parent`. The identity
 * bootstrap fires on mount, so the origin is known early — exactly, and
 * independent of `document.referrer`.
 *
 * Consumed by `screen/open`'s "Back to Shop" navigation, which needs an
 * absolute storefront URL to safely navigate the top window (a relative
 * `top.location = "/"` would resolve against the iframe's own origin).
 *
 * Persisted in sessionStorage because `screen/open` navigates the iframe with
 * a full-page reload (window.location), which resets this module's in-memory
 * value. Without persistence, the detail screen starts with no origin and
 * relies on the next host message to re-capture it — a window in which "Back
 * to Shop" would fall through to `document.referrer`, which after that
 * self-navigation points at the IFRAME's own origin, not the storefront.
 * sessionStorage is same-origin + per-tab, so it survives those reloads.
 *
 * Lives in its own leaf module (no imports) so both `postMessageActions`
 * (writer) and `screenOpen` (reader) can use it without a circular import
 * through the action registry.
 */
const STORAGE_KEY = "tapcart-storefront-bridge:parent-origin";
let parentOrigin = null;
const readSession = () => {
    var _a, _b;
    if (typeof window === "undefined")
        return null;
    try {
        return (_b = (_a = window.sessionStorage) === null || _a === void 0 ? void 0 : _a.getItem(STORAGE_KEY)) !== null && _b !== void 0 ? _b : null;
    }
    catch (_c) {
        return null;
    }
};
export const setParentOrigin = (origin) => {
    var _a;
    // "null" is the literal string some browsers send for opaque origins.
    if (!origin || origin === "null")
        return;
    parentOrigin = origin;
    if (typeof window === "undefined")
        return;
    try {
        (_a = window.sessionStorage) === null || _a === void 0 ? void 0 : _a.setItem(STORAGE_KEY, origin);
    }
    catch (_b) {
        // sessionStorage unavailable (privacy mode) — in-memory value still works
        // within a single page load.
    }
};
export const getParentOrigin = () => {
    if (!parentOrigin) {
        parentOrigin = readSession();
    }
    return parentOrigin;
};
